import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useDaoConfig } from '@/hooks/useDaoConfig';

import { animateElement, type AnimationType } from './../animations';
import type { WelcomeStepRef } from './types';

export const WelcomeStep2 = forwardRef<WelcomeStepRef>((_, ref) => {
  const { daoName, clientRole, workerRolePlural, coordinatorRole } = useDaoConfig();
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const elements = containerRef.current?.querySelectorAll<HTMLElement>('[data-animate-in]');

    elements?.forEach((element) => {
      const animation = element.dataset.animateIn as AnimationType | undefined;

      if (animation) {
        animateElement(element, animation).catch(() => {});
      }
    });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      animateOut: async () => {
        const elements = containerRef.current?.querySelectorAll<HTMLElement>('[data-animate-out]');

        if (!elements) return;

        await Promise.all(
          Array.from(elements).map((element) => {
            const animation = element.dataset.animateOut as AnimationType | undefined;

            if (!animation) return Promise.resolve();

            return animateElement(element, animation);
          }),
        );
      },
    }),
    [],
  );

  return (
    <div ref={containerRef} className="stepContainer">
      <h1 data-animate-in="textFadeIn" data-animate-out="textFadeOut" className="opacity-0">
        {daoName}, a marketplace built around accountability.
      </h1>
      <p
        data-animate-in="textFadeIn"
        data-animate-out="textFadeOut"
        className="opacity-0 text-[--text-dark-secondary] font-normal"
      >
        Think of {daoName} as a marketplace with three roles.
      </p>

      {/* Image - animations container */}
      <div className="animationsContainer">
        <div className="relative ">
          <div
            data-animate-in="scaleUp"
            data-animate-out="scaleDown"
            className="h-[167px] aspect-square rounded-full glassEffect border-0"
          >
            <img
              src="./public/images/onboarding/gradientBorder_circle.svg"
              // alt="gradient border circle"
            />
          </div>

          {/* Blue bubble - top left */}
          <div
            data-animate-in="fadeIn"
            data-animate-out="fadeOut"
            className="
              z-50 absolute  opacity-0
              top-0 left-0 -translate-x-1/2 -translate-y-1/2
              h-[37px] aspect-square max-w-[37px]
              rounded-full bg-[--colors-cyan-600]
            "
          >
            <div className="relative h-full flex items-center justify-end">
              <div className="absolute -top-[16px] -right-[16px] px-8 py-6 pr-[68px] h-fit w-fit bg-[--colors-cyan-100] rounded-full">
                <p className="text-nowrap text-right text-[--colors-cyan-600]">
                  {clientRole} brings the project
                </p>
              </div>
              {/* Visible blue dot */}
              <span
                className="
                  z-50 absolute top-[20px] left-[16px]
                  -translate-x-1/2 -translate-y-1/2
                  h-[37px] aspect-square
                  rounded-full bg-[--colors-cyan-600]
                "
              />
            </div>
          </div>

          {/* Green bubble - bottom right */}
          <div
            data-animate-in="fadeIn"
            data-animate-out="fadeOut"
            className="
              z-50 absolute opacity-0
              top-full left-full -translate-x-1/2 -translate-y-1/2
              h-[37px] aspect-square 
              rounded-full bg-[--colors-emerald-500]
            "
          >
            <div className="relative h-full flex items-center justify-start">
              <div className="absolute -top-[16px] -left-[16px] px-8 py-6 pl-[68px] h-fit w-fit bg-[--colors-emerald-100] rounded-full">
                <p className="text-nowrap text-right text-[--state-success-active]">
                  {workerRolePlural} do the work
                </p>
              </div>
            </div>
            {/* Visible green dot */}
            <span
              className="
                z-50 absolute top-[20px] left-[20px]
                -translate-x-1/2 -translate-y-1/2
                h-[37px] aspect-square 
                rounded-full bg-[--colors-emerald-500]
              "
            />
          </div>

          {/* Purple bubble - center */}
          <div
            data-animate-in="fadeIn"
            data-animate-out="fadeOut"
            className="
              z-50 absolute  opacity-0
              top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
              px-[--spacing-8] py-[--spacing-5]
              h-fit rounded-full bg-[color-mix(in_srgb,var(--colors-fuchsia-400)_40%,transparent)]
              border border-[--colors-alpha-dark-200]
            "
          >
            <div className="relative flex flex-col items-center justify-start text-nowrap text-center text-[--colors-fuchsia-800]">
              <p>{coordinatorRole}</p>
              <p className="text-xs">handles the connection between them</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

WelcomeStep2.displayName = 'WelcomeStep2';
